/**
 * netStore — 联机会话状态（客户端单一数据通道）。
 *
 * netClient（WebSocket 层）负责写入；场景订阅渲染。
 * 瞬时消息流（STATE_UPDATE / PLAYER_PRESENCE …）不进入此 store，
 * 由 netClient.on(type, handler) 直接分发给订阅场景。
 *
 * 设计对齐：docs/multiplayer/PROTOCOL.md（v3 客户端状态机）。
 */
import { atom } from 'nanostores';
import type { OnlinePlayerCount } from '@shared/net/constants';
import type {
  GameOverPayload,
  GameStartPayload,
  PartyMemberInfo,
  PublicPlayer,
  QueueStatusPayload,
  RematchUpdatePayload,
  RoomClosedPayload,
} from '@shared/net/messages';

/** 联机连接状态机（PROTOCOL.md §6）。 */
export type NetStatus =
  | 'offline' // 从未连接（初始）
  | 'connecting' // socket 建立中 / HELLO 握手 / 断线重连中
  | 'idle' // 已连接，无队伍 / 队列 / 对局
  | 'party' // 在队伍中（未排队）
  | 'in-queue' // 匹配队列中
  | 'matched' // MATCH_FOUND 已至，开局倒计时
  | 'in-match' // GAME_START 已至，对局进行中（含终局投票窗口）
  | 'error'; // 连接失败（仍可能后台重试）

export interface NetParty {
  code: string;
  leaderId: string;
  members: PartyMemberInfo[];
}

export interface NetMatch {
  matchId: string;
  playerCount: OnlinePlayerCount;
  yourSeat: number;
  /** Seat-order roster. */
  players: PublicPlayer[];
  /** Client-clock ms when GAME_START is expected（倒计时 UI）。 */
  startsAt: number;
}

export interface NetState {
  status: NetStatus;
  playerId: string | null;
  /** Server-sanitized effective nickname（HELLO_ACK 后）。 */
  nickname: string;
  /** 下次排队期望的人数模式（2/3/6）。 */
  playerCount: OnlinePlayerCount;
  party: NetParty | null;
  match: NetMatch | null;
  /** Server timestamp when the queue was joined（ elapsed UI）。 */
  queuedAt: number | null;
  /**
   * v3.1（§6.4）：最近一帧 QUEUE_STATUS + 本地接收时刻。
   *  waitedMs / fillAiInMs 随时间流逝由 UI 自减，帧仅作锚点。
   */
  queueStatus: (QueueStatusPayload & { receivedAt: number }) | null;
  /** serverNow − clientNow 估计（PONG 信封时间戳 + RTT/2）。 */
  clockOffset: number;
  rttMs: number | null;
  /** GAME_START / STATE_SYNC 快照（GameScene 接力棒）。 */
  gameStart: GameStartPayload | null;
  gameOver: GameOverPayload | null;
  rematch: RematchUpdatePayload | null;
  roomClosed: RoomClosedPayload | null;
  /** 最近一次服务端 ERROR 码（UI 映射 i18n 后清除）。 */
  error: string | null;
}

export const $net = atom<NetState>({
  status: 'offline',
  playerId: null,
  nickname: '',
  playerCount: 2,
  party: null,
  match: null,
  queuedAt: null,
  queueStatus: null,
  clockOffset: 0,
  rttMs: null,
  gameStart: null,
  gameOver: null,
  rematch: null,
  roomClosed: null,
  error: null,
});

export function patchNet(patch: Partial<NetState>): void {
  $net.set({ ...$net.get(), ...patch });
}

export function setNetStatus(status: NetStatus, patch: Partial<NetState> = {}): void {
  patchNet({ ...patch, status });
}

export function setNetPlayerCount(playerCount: OnlinePlayerCount): void {
  patchNet({ playerCount });
}

/** 离开对局/房间的本地清理（LEAVE_GAME 意图发出后调用）。 */
export function clearNetMatch(): void {
  patchNet({
    match: null,
    gameStart: null,
    gameOver: null,
    rematch: null,
    roomClosed: null,
  });
}
